
import {
  cleanName,
  formatDate,
  getDisplayDueAmount,
  getDisplayOrderAmount,
  getOrderTypeLabel,
  normalizeOrderType,
  safeAmount,
} from "./PosOrderHelper";

function TypeBadge({ type }) {
  const normalizedType = normalizeOrderType(type);

  const styles = {
    takeaway: "bg-blue-500 text-white",
    home_delivery: "bg-purple-500 text-white",
    dining: "bg-green-500 text-white",
    room_order: "bg-orange-500 text-white",
  };

  const badgeStyle =
    styles[normalizedType] || "bg-gray-500 text-white";

  return (
    <span
      className={`text-xs font-bold px-3 py-2 rounded-lg ${badgeStyle}`}
    >
      {getOrderTypeLabel(type)}
    </span>
  );
}

function DeliveryCell({
  status,
  orderId,
  orderStatus,
  onAssign,
}) {
  const normalizedStatus = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  const normalizedOrderStatus = String(orderStatus || "")
    .trim()
    .toLowerCase();

  const isHold = normalizedOrderStatus === "hold";

  const isSuccessful = [
    "successful",
    "successfull",
    "success",
    "completed",
    "delivered",
  ].includes(normalizedStatus);

  const isAssigned = [
    "assigned",
    "accepted",
    "picked_up",
    "out_for_delivery",
  ].includes(normalizedStatus);

  const isUnassigned = [
    "unassigned",
    "not_assigned",
    "pending",
    "n",
    "",
  ].includes(normalizedStatus);

  if (isSuccessful) {
    return (
      <span className="inline-flex items-center justify-center gap-1 rounded-full bg-green-500 border border-green-600 px-3 py-1.5 text-xs font-bold text-white whitespace-nowrap">
        SUCCESSFUL
      </span>
    );
  }

  if (isAssigned) {
    return (
      <span className="inline-flex items-center justify-center gap-1 rounded-full bg-blue-500 border border-blue-600 px-3 py-1.5 text-xs font-bold text-white whitespace-nowrap">
        <span className="h-2 w-2 rounded-full bg-white" />
        ASSIGNED
      </span>
    );
  }

  if (isUnassigned) {
    return (
      <button
        type="button"
        disabled={isHold}
        onClick={(event) => {
          event.stopPropagation();

          if (isHold) return;

          onAssign?.(orderId);
        }}
        className={`inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-bold whitespace-nowrap transition-all ${
          isHold
            ? "bg-yellow-100 border border-yellow-300 text-yellow-700 cursor-not-allowed"
            : "bg-yellow-400 border border-yellow-500 text-yellow-900 hover:bg-yellow-500 cursor-pointer"
        }`}
      >
        {isHold ? "ORDER ON HOLD" : "ASSIGN TO PACKKY"}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center justify-center rounded-full bg-gray-100 border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-600 whitespace-nowrap">
      {String(status || "N/A")
        .replace(/_/g, " ")
        .toUpperCase()}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className="border-b border-gray-50 animate-pulse">
      {Array.from({ length: 10 }).map((_, index) => (
        <td
          key={index}
          className="px-4 py-3 border border-gray-200"
        >
          <div className="h-3 bg-gray-100 rounded-md w-full" />
        </td>
      ))}
    </tr>
  );
}

export default function PosOrderTable({
  loading,
  filteredOrders,
  selected,
  setSelected,
  tabletMode,
  canUsePos,
  onAssignDelivery,
}) {
  const safeFilteredOrders = Array.isArray(filteredOrders)
    ? filteredOrders
    : [];

  return (
    <>
    
    <div className="app-container-pos bg-white border border-gray-100 shadow-sm flex-1 min-h-0 max-w-full relative">
      <div className="table-wrapper">
        <table
          className={`border-collapse border border-gray-500 min-w-[1200px] w-full ${
            tabletMode ? "text-xs" : "text-sm"
          }`}
        >
          <thead>
            <tr className="border-b border-gray-800 bg-green-100 sticky top-0 z-10">
              {[
                "ODR ID",
                "BILL NO",
                "DATE",
                "NAME",
                "TYPE",
                "TBL",
                "AMOUNT",
                "PAID",
                "DUE",
                "DELIVERY",
              ].map((heading) => (
                <th
                  key={heading}
                  className="px-5 py-4 border border-gray-600 text-left text-[13px] font-mono font-semibold tracking-wide whitespace-nowrap rounded"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <SkeletonRow key={index} />
              ))
            ) : safeFilteredOrders.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="text-center py-16 text-gray-600 text-sm"
                >
                  No orders found
                </td>
              </tr>
            ) : (
              safeFilteredOrders.map((order, index) => {
                const orderStatus = String(
                  order?.status || "",
                ).toLowerCase();

                const isCompleted = orderStatus === "completed";

                const orderId =
                  order?.orderid || order?.orderId || "";

                const deliveryStatus =
                  order?.delivery_partner_status ||
                  order?.deliveryStatus ||
                  order?.delivery_status ||
                  "";

                const isHomeDelivery =
                  normalizeOrderType(order?.orderType) ===
                  "home_delivery";

                const paidAmount = safeAmount(
                  order?.paid_amount || order?.pay_amount,
                );

                return (
                  <tr
                    key={order?.id || orderId || index}
                    onClick={() => {
                      if (!canUsePos()) return;

                      setSelected(
                        selected === index ? null : index,
                      );
                    }}
                    className={`border border-gray-600 cursor-pointer transition-colors duration-100 ${
                      selected === index
                        ? "bg-emerald-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="px-4 py-3 border border-gray-600 font-mono font-semibold text-gray-900">
                      {isCompleted
                        ? order?.daily_order_id ||
                          orderId ||
                          "—"
                        : order?.daily_order_id || "HOLD"}
                    </td>

                    <td className="px-4 py-3 border border-gray-600 font-mono font-semibold text-gray-900">
                      {orderId || "—"}
                    </td>

                    <td className="px-4 py-3 border border-gray-600 font-mono font-semibold text-gray-900">
                      <div className="flex flex-col">
                        <span className="text-gray-900 font-medium text-sm">
                          {formatDate(order?.order_date) || "—"}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3 max-w-[150px] border border-gray-600 font-mono font-semibold text-gray-900">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 truncate block">
                          {cleanName(order?.customer_name) || (
                            <span className="text-gray-600">
                              No Name
                            </span>
                          )}
                        </span>

                        <span className="font-mono text-gray-900 text-[14px] leading-tight">
                          {order?.customer_mobile || "No Mobile"}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3 border border-gray-600 font-mono font-semibold text-gray-900">
                      <TypeBadge type={order?.orderType} />
                    </td>

                    <td className="px-4 py-3 text-center border border-gray-600 font-mono font-semibold text-gray-900">
                      {order?.table_no || "—"}
                    </td>

                    <td className="px-4 py-3 border border-gray-600 font-mono font-semibold text-gray-900">
                      ₹
                      {getDisplayOrderAmount(order).toFixed(0)}
                    </td>

                    <td className="px-4 py-3 border border-gray-600 font-mono font-semibold text-gray-900">
                      ₹{paidAmount.toFixed(0)}
                    </td>

                    <td className="px-4 py-3 text-center border border-gray-600 font-mono font-semibold text-gray-900">
                      <span className="inline-flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-lg min-w-[36px] h-6 px-1.5">
                        ₹
                        {Math.round(
                          getDisplayDueAmount(order),
                        )}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center border border-gray-600 font-mono font-semibold text-gray-900">
                      {isHomeDelivery ? (
                        <DeliveryCell
                          status={deliveryStatus}
                          orderId={orderId}
                          orderStatus={order?.status}
                          onAssign={onAssignDelivery}
                        />
                      ) : (
                        <span className="inline-flex rounded-full bg-gray-100 border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-500">
                          N/A
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <span className="text-8xl font-black text-gray-500 select-none tracking-widest opacity-10">
          Anlook POS
        </span>
      </div>
    </div>
    </>
  );
}
